import { Plus, Trash2 } from 'lucide-react';
import React from 'react';
import type { Control, FieldPath, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { CarrierNameSelect } from '@/components/carrier-directory/CarrierNameSelect';
import {
  benefitPriorityOrders,
  benefitTypeLabels,
} from '@/components/coverage/shared/constants';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BenefitType,
  CoverageCategory as CoverageCategoryEnum,
  PolicyType,
} from '@/services/types/coverage-types';

import type { CoverageCategory, CoverageFormData } from './CoverageStep';
import { IngestionNotFoundBanner } from '../../layout';

/** Maps ingestion category strings to coverage enum values */
const CATEGORY_TO_ENUM: Record<CoverageCategory, CoverageCategoryEnum> = {
  firstParty: CoverageCategoryEnum.FIRST_PARTY,
  thirdParty: CoverageCategoryEnum.THIRD_PARTY,
  health: CoverageCategoryEnum.FIRST_PARTY_HEALTH,
};

/** Get benefit type options for a category using shared constants */
const getBenefitTypeOptions = (category: CoverageCategory) => {
  const enumCategory = CATEGORY_TO_ENUM[category];
  return benefitPriorityOrders[enumCategory].map((type) => ({
    value: type,
    label: benefitTypeLabels[type],
  }));
};

interface CoverageEntryCardProps {
  index: number;
  category: CoverageCategory;
  control: Control<CoverageFormData>;
  setValue: UseFormSetValue<CoverageFormData>;
}

export const CoverageEntryCard: React.FC<CoverageEntryCardProps> = ({
  index,
  category,
  control,
  setValue,
}) => {
  const benefitOptions = getBenefitTypeOptions(category);
  const fieldPrefix = `${category}Coverages.${index}`;

  return (
    <Card className="border-zinc-200">
      <CardContent className="p-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {/* ===== POLICY INFO SECTION ===== */}

          {/* Row 1: Policy Type (required) */}
          <div>
            <Label className="text-xs">
              Policy Type <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={`${fieldPrefix}.policyType` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Select
                  value={(field.value as PolicyType | null) || undefined}
                  onValueChange={field.onChange}
                  disabled={category === 'health'}
                >
                  <SelectTrigger className="mt-1 h-8 text-xs">
                    <SelectValue placeholder="Select policy type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PolicyType.AUTO}>Auto</SelectItem>
                    {category === 'health' && (
                      <SelectItem value={PolicyType.HEALTH}>Health</SelectItem>
                    )}
                    <SelectItem value={PolicyType.HOMEOWNERS}>Homeowners</SelectItem>
                    <SelectItem value={PolicyType.COMMERCIAL}>Commercial</SelectItem>
                    <SelectItem value={PolicyType.UMBRELLA}>Umbrella</SelectItem>
                    <SelectItem value={PolicyType.OTHER}>Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Row 1: Carrier Name (required) */}
          <div>
            <Label className="text-xs">
              Carrier Name <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={`${fieldPrefix}.carrierName` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <div className="mt-1">
                  <CarrierNameSelect
                    value={(field.value as string) || ''}
                    onChange={(name, carrierId) => {
                      field.onChange(name);
                      setValue(`${fieldPrefix}.carrierId` as FieldPath<CoverageFormData>, carrierId || null);
                    }}
                    onSelectDirectory={(carrierId, divisionId) => {
                      setValue(`${fieldPrefix}.carrierId` as FieldPath<CoverageFormData>, carrierId);
                      setValue(`${fieldPrefix}.divisionId` as FieldPath<CoverageFormData>, divisionId || null);
                    }}
                    className="h-8 text-xs w-full justify-start"
                    placeholder="Select carrier"
                  />
                </div>
              )}
            />
          </div>

          {/* Row 2: Policy Number */}
          <div>
            <Label className="text-xs">Policy Number</Label>
            <Controller
              name={`${fieldPrefix}.policyNumber` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="Enter policy number"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* Row 2: Named Insured */}
          <div>
            <Label className="text-xs">Named Insured</Label>
            <Controller
              name={`${fieldPrefix}.namedInsured` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="Enter named insured"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* ===== CLAIM INFO SECTION ===== */}

          {/* Row 3: Claim Number */}
          <div>
            <Label className="text-xs">Claim Number</Label>
            <Controller
              name={`${fieldPrefix}.claimNumber` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="Enter claim number"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* Row 3: Benefit Type */}
          <div>
            <Label className="text-xs">Benefit Type</Label>
            <Controller
              name={`${fieldPrefix}.benefitType` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Select
                  value={(field.value as BenefitType | null) || undefined}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger className="mt-1 h-8 text-xs">
                    <SelectValue placeholder="Select benefit type" />
                  </SelectTrigger>
                  <SelectContent>
                    {benefitOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Row 4: Policy Limit */}
          <div>
            <Label className="text-xs">Policy Limit</Label>
            <Controller
              name={`${fieldPrefix}.policyLimit` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="e.g., $100,000"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* Row 4: Empty space */}
          <div />

          {/* ===== ADJUSTER CONTACT SECTION ===== */}

          {/* Section Separator */}
          <div className="col-span-2 pt-2 mt-1 border-t border-zinc-100">
            <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wide">
              Adjuster Contact
            </span>
          </div>

          {/* Row 5: Adjuster Name */}
          <div>
            <Label className="text-xs">Adjuster Name</Label>
            <Controller
              name={`${fieldPrefix}.adjusterName` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="Enter adjuster name"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* Row 5: Adjuster Phone */}
          <div>
            <Label className="text-xs">Adjuster Phone</Label>
            <Controller
              name={`${fieldPrefix}.adjusterPhone` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="555-123-4567"
                  type="tel"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* Row 6: Adjuster Email */}
          <div>
            <Label className="text-xs">Adjuster Email</Label>
            <Controller
              name={`${fieldPrefix}.adjusterEmail` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="adjuster@insurance.com"
                  type="email"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>

          {/* Row 6: Adjuster Fax */}
          <div>
            <Label className="text-xs">Adjuster Fax</Label>
            <Controller
              name={`${fieldPrefix}.adjusterFax` as FieldPath<CoverageFormData>}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={(field.value as string) || ''}
                  placeholder="555-123-4568"
                  type="tel"
                  className="mt-1 h-8 text-xs"
                />
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface CoverageSectionProps {
  title: string;
  category: CoverageCategory;
  control: Control<CoverageFormData>;
  setValue: UseFormSetValue<CoverageFormData>;
  fields: { id: string }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  notFoundChecked: boolean;
  onNotFoundChange: (checked: boolean) => void;
  notFoundLabel: string;
  addButtonLabel: string;
}

export const CoverageSection: React.FC<CoverageSectionProps> = ({
  title,
  category,
  control,
  setValue,
  fields,
  onAdd,
  onRemove,
  notFoundChecked,
  onNotFoundChange,
  notFoundLabel,
  addButtonLabel,
}) => {
  return (
    <Card>
      <CardContent className="p-2">
        <div className="space-y-2">
          {/* Section header */}
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-zinc-900">{title}</h3>
            {fields.length === 0 && (
              <IngestionNotFoundBanner
                id={`${category}-not-found`}
                checked={notFoundChecked}
                onCheckedChange={onNotFoundChange}
                label={notFoundLabel}
              />
            )}
          </div>

          {/* Coverage entries (hidden when not found) */}
          {!notFoundChecked && (
            <>
              {/* Coverage cards */}
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-start gap-1">
                    <div className="flex-1 min-w-0">
                      <CoverageEntryCard
                        index={index}
                        category={category}
                        control={control}
                        setValue={setValue}
                      />
                    </div>
                    {fields.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onRemove(index);
                        }}
                        className="h-6 w-6 p-0 mt-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add button */}
              <Button
                type="button"
                variant="outline"
                onClick={onAdd}
                size="sm"
                className="w-full h-7 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {addButtonLabel}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
